import { useState, useEffect } from 'react'
import { useParams, useNavigate } from 'react-router-dom'
import { Container, Row, Col, Card, Table, Spinner, Alert, Badge, ProgressBar } from 'react-bootstrap'
import client from '../../api/client'

function HFModelStats() {
  const { id } = useParams()
  const navigate = useNavigate()
  const [model, setModel] = useState(null)
  const [stats, setStats] = useState(null)
  const [votes, setVotes] = useState(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState('')

  useEffect(() => {
    fetchStats()
  }, [id])

  const fetchStats = async () => {
    setLoading(true)
    try {
      const [modelResponse, statsResponse, votesResponse] = await Promise.all([
        client.get(`/admin/api/v1/hf-models/${id}`),
        client.get(`/admin/api/v1/hf-models/${id}/stats`),
        client.get(`/admin/api/v1/hf-models/${id}/votes`)
      ])
      setModel(modelResponse.data)
      setStats(statsResponse.data)
      setVotes(votesResponse.data)
    } catch (err) {
      console.error('Error fetching stats:', err)
      setError('Erreur lors du chargement des statistiques')
    } finally {
      setLoading(false)
    }
  }

  const formatDate = (dateStr) => {
    if (!dateStr) return '-'
    return new Date(dateStr).toLocaleDateString('fr-FR', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    })
  }

  const getPipelineLabel = (tag) => {
    const labels = {
      'text-generation': 'Génération de texte',
      'conversational': 'Conversationnel',
      'image-generation': 'Génération d\'images',
      'text-to-image': 'Texte vers image',
      'automatic-speech-recognition': 'Reconnaissance vocale',
      'audio-classification': 'Classification audio'
    }
    return labels[tag] || tag
  }

  if (loading) {
    return (
      <Container fluid>
        <div className="d-flex justify-content-center align-items-center" style={{ height: '50vh' }}>
          <Spinner animation="border" role="status">
            <span className="visually-hidden">Chargement...</span>
          </Spinner>
        </div>
      </Container>
    )
  }

  if (error || !model || !stats || !votes) {
    return (
      <Container fluid>
        <Alert variant="danger">{error || 'Modèle introuvable'}</Alert>
      </Container>
    )
  }

  const maxDailyUses = Math.max(...stats.daily_breakdown.map(d => d.count), 1)
  const sevenDaysAgo = new Date()
  sevenDaysAgo.setDate(sevenDaysAgo.getDate() - 7)
  const usesThisWeek = stats.daily_breakdown
    .filter(d => new Date(d.date) >= sevenDaysAgo)
    .reduce((sum, d) => sum + d.count, 0)

  return (
    <Container fluid>
      <Row className="mb-4">
        <Col>
          <h2>📊 Statistiques du modèle HF</h2>
        </Col>
      </Row>

      {/* Model Info Header */}
      <Card className="mb-4">
        <Card.Header>
          <strong>{model.name}</strong>
        </Card.Header>
        <Card.Body>
          {model.description && <p className="text-muted">{model.description}</p>}
          <Row>
            <Col md={3}>
              <small className="text-muted">HF Model ID:</small><br />
              <code>{model.hf_model_id}</code>
            </Col>
            <Col md={2}>
              <small className="text-muted">Pipeline Tag:</small><br />
              <Badge bg="info">{getPipelineLabel(model.pipeline_tag)}</Badge>
            </Col>
            <Col md={2}>
              <small className="text-muted">Téléchargements HF:</small><br />
              <strong>{model.downloads ? model.downloads.toLocaleString() : '-'}</strong>
            </Col>
            <Col md={2}>
              <small className="text-muted">Likes HF:</small><br />
              <strong>{model.likes ? model.likes.toLocaleString() : '-'}</strong>
            </Col>
            <Col md={3}>
              <small className="text-muted">Statut:</small><br />
              <Badge bg={model.active ? 'success' : 'secondary'}>
                {model.active ? 'Actif' : 'Inactif'}
              </Badge>
            </Col>
          </Row>
        </Card.Body>
      </Card>

      {/* Usage Statistics */}
      <Card className="mb-4">
        <Card.Header>
          <strong>📈 Statistiques d'utilisation</strong>
        </Card.Header>
        <Card.Body>
          <Row className="mb-4">
            <Col md={3}>
              <div className="text-center">
                <h4>{stats.total_uses}</h4>
                <small className="text-muted">Utilisations totales</small>
              </div>
            </Col>
            <Col md={3}>
              <div className="text-center">
                <h4>{stats.uses_today}</h4>
                <small className="text-muted">Aujourd'hui</small>
              </div>
            </Col>
            <Col md={3}>
              <div className="text-center">
                <h4>{usesThisWeek}</h4>
                <small className="text-muted">Cette semaine</small>
              </div>
            </Col>
            <Col md={3}>
              <div className="text-center">
                <h4>{stats.unique_users}</h4>
                <small className="text-muted">Utilisateurs uniques</small>
              </div>
            </Col>
          </Row>

          <h6 className="mb-3">Utilisations quotidiennes (30 derniers jours)</h6>
          <div>
            {stats.daily_breakdown.slice().reverse().map((day, index) => {
              const percentage = (day.count / maxDailyUses) * 100
              return (
                <div key={index} className="mb-2">
                  <div className="d-flex justify-content-between mb-1">
                    <small>{formatDate(day.date)}</small>
                    <small><strong>{day.count}</strong> utilisation{day.count > 1 ? 's' : ''}</small>
                  </div>
                  <ProgressBar
                    now={percentage}
                    variant="primary"
                  />
                </div>
              )
            })}
          </div>
        </Card.Body>
      </Card>

      {/* Vote Breakdown */}
      <Card className="mb-4">
        <Card.Header>
          <strong>👍👎 Votes des utilisateurs</strong>
        </Card.Header>
        <Card.Body>
          <Row className="mb-4">
            <Col md={4}>
              <div className="text-center">
                <h4 className="text-success">{votes.upvotes}</h4>
                <small className="text-muted">👍 Upvotes</small>
              </div>
            </Col>
            <Col md={4}>
              <div className="text-center">
                <h4 className="text-danger">{votes.downvotes}</h4>
                <small className="text-muted">👎 Downvotes</small>
              </div>
            </Col>
            <Col md={4}>
              <div className="text-center">
                <h4 className={votes.net_score > 0 ? 'text-success' : votes.net_score < 0 ? 'text-danger' : ''}>
                  {votes.net_score > 0 ? '+' : ''}{votes.net_score}
                </h4>
                <small className="text-muted">Score net</small>
              </div>
            </Col>
          </Row>

          {votes.upvotes + votes.downvotes > 0 && (
            <div className="mb-4">
              <h6 className="mb-2">Distribution des votes</h6>
              <div className="d-flex align-items-center gap-2">
                <span className="small text-success">Upvotes</span>
                <div className="flex-grow-1">
                  <ProgressBar>
                    <ProgressBar
                      variant="success"
                      now={(votes.upvotes / (votes.upvotes + votes.downvotes)) * 100}
                      label={`${votes.upvotes}`}
                    />
                    <ProgressBar
                      variant="danger"
                      now={(votes.downvotes / (votes.upvotes + votes.downvotes)) * 100}
                      label={`${votes.downvotes}`}
                    />
                  </ProgressBar>
                </div>
                <span className="small text-danger">Downvotes</span>
              </div>
            </div>
          )}

          {votes.recent_votes && votes.recent_votes.length > 0 && (
            <div>
              <h6 className="mb-3">Votes récents</h6>
              <Table striped bordered hover size="sm">
                <thead>
                  <tr>
                    <th>Utilisateur</th>
                    <th>Vote</th>
                    <th>Date</th>
                  </tr>
                </thead>
                <tbody>
                  {votes.recent_votes.map((vote, index) => (
                    <tr key={index}>
                      <td>
                        <small>{vote.user_email}</small>
                      </td>
                      <td>
                        <Badge bg={vote.vote === 1 ? 'success' : 'danger'}>
                          {vote.vote === 1 ? '👍 Upvote' : '👎 Downvote'}
                        </Badge>
                      </td>
                      <td>
                        <small className="text-muted">{formatDate(vote.created_at)}</small>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </Table>
            </div>
          )}

          {votes.recent_votes && votes.recent_votes.length === 0 && (
            <Alert variant="info" className="mb-0">
              Aucun vote enregistré pour ce modèle
            </Alert>
          )}
        </Card.Body>
      </Card>

      <div className="mb-4">
        <button className="btn btn-outline-secondary" onClick={() => navigate('/hf-models')}>
          ← Retour à la liste
        </button>
      </div>
    </Container>
  )
}

export default HFModelStats
